/**
 * A small history-based router.
 *
 * Deliberately not a routing library. The application has about twenty routes,
 * two levels deep by design (docs/08-uiux.md §3), and the kiosk bundle has a
 * 150 KB budget it has to share with a PDF viewer and a signature pad. Eighty
 * lines here costs less than a dependency, and there is no configuration
 * surface to learn.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface RouterValue {
  path: string
  params: Record<string, string>
  query: URLSearchParams
  navigate: (to: string, options?: { replace?: boolean }) => void
  back: () => void
}

const RouterContext = createContext<RouterValue | null>(null)

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname)
  const [search, setSearch] = useState(() => window.location.search)

  useEffect(() => {
    const onPopState = () => {
      setPath(window.location.pathname)
      setSearch(window.location.search)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (options?.replace) window.history.replaceState({}, '', to)
    else window.history.pushState({}, '', to)
    setPath(window.location.pathname)
    setSearch(window.location.search)
    window.scrollTo({ top: 0 })
  }, [])

  const value = useMemo<RouterValue>(
    () => ({
      path,
      params: {},
      query: new URLSearchParams(search),
      navigate,
      back: () => window.history.back(),
    }),
    [path, search, navigate],
  )

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

export function useRouter() {
  const context = useContext(RouterContext)
  if (!context) throw new Error('useRouter must be used inside a RouterProvider')
  return context
}

/**
 * Match `/admin/templates/:id/versions/:versionId` against the current path.
 * Returns the captured params, or null when it does not match.
 */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean)
  const pathParts = path.split('/').filter(Boolean)

  // A trailing * matches the rest, which is how nested layouts opt in.
  const wildcard = patternParts.at(-1) === '*'
  if (!wildcard && patternParts.length !== pathParts.length) return null
  if (wildcard && pathParts.length < patternParts.length - 1) return null

  const params: Record<string, string> = {}
  for (const [index, part] of patternParts.entries()) {
    if (part === '*') return params
    const actual = pathParts[index]
    if (part.startsWith(':')) {
      if (!actual) return null
      params[part.slice(1)] = decodeURIComponent(actual)
    } else if (part !== actual) {
      return null
    }
  }
  return params
}

export function useMatch(pattern: string) {
  const { path } = useRouter()
  return useMemo(() => matchPath(pattern, path), [pattern, path])
}

export function Link({
  to,
  children,
  className,
  replace,
  onClick,
  ...rest
}: {
  to: string
  children: ReactNode
  className?: string
  replace?: boolean
  onClick?: () => void
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>) {
  const { navigate } = useRouter()
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        // Let modified clicks open a new tab, as any real link would.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onClick?.()
        navigate(to, { replace })
      }}
      {...rest}
    >
      {children}
    </a>
  )
}

/** Render the first child route whose pattern matches. */
export function Routes({
  routes,
  notFound,
}: {
  routes: { path: string; element: (params: Record<string, string>) => ReactNode }[]
  notFound?: ReactNode
}) {
  const { path } = useRouter()
  for (const route of routes) {
    const params = matchPath(route.path, path)
    if (params) return <>{route.element(params)}</>
  }
  return <>{notFound ?? null}</>
}
